import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ManagementService} from '../management.service';
import {DryRunResult} from '../types';
import {sentimentLabel} from '../../../utils/sentiment';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';

/** Runs the prompt for real (two synchronous LLM calls, up to ~2 min) and shows the answer + detected brands. */
@Component({
  selector: 'app-dry-run-modal',
  imports: [ButtonComponent, LoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './dry-run-modal.component.scss',
  template: `
    <div class="dry-run-modal">
      <div class="title">Test prompt</div>
      <div class="prompt-text">{{ data.promptText }}</div>
      @if (loading()) {
        <div class="loading-box">
          <app-loader></app-loader>
          <span class="hint">Running the prompt against the LLM — this can take up to two minutes…</span>
        </div>
      } @else if (error()) {
        <div class="error">{{ error() }}</div>
      } @else if (result()) {
        <div class="response">{{ result()!.responseText }}</div>
        <div class="title">Detected brands</div>
        @if (mentionEntries().length) {
          <div class="mentions">
            @for (mention of mentionEntries(); track mention.brandKey) {
              <span class="mention-chip" title="How this brand was mentioned in the answer, by sentiment">{{ mention.brandKey }} · {{ sentimentLabel(mention) }}</span>
            }
          </div>
        } @else {
          <span class="hint">No brands from the brands list were detected. If a brand appears in the answer above, add it (or an alias) in the Brands tab.</span>
        }
      }
      <div class="actions">
        <app-button text="Close" class="secondary" (clicked)="dialogRef.close()"></app-button>
      </div>
    </div>
  `,
})
export class DryRunModalComponent {
  protected readonly data = inject<{topic: string; promptText: string}>(MAT_DIALOG_DATA);
  protected readonly dialogRef = inject(MatDialogRef<DryRunModalComponent>);
  private readonly api = inject(ManagementService);

  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly result = signal<DryRunResult | null>(null);
  protected readonly mentionEntries = signal<{brandKey: string; positive: number; neutral: number; negative: number}[]>([]);

  protected readonly sentimentLabel = sentimentLabel;

  constructor() {
    // The run costs real LLM money — block backdrop/Escape closes while it is in flight so the
    // user can't dismiss and fire a second one (same pattern as research-prompt).
    this.dialogRef.disableClose = true;
    this.api.dryRunAiPrompt(this.data.topic, this.data.promptText).subscribe({
      next: result => {
        this.result.set(result);
        this.mentionEntries.set(Object.entries(result.mentions ?? {}).map(([brandKey, counts]) => ({brandKey, ...counts})));
        this.loading.set(false);
        this.dialogRef.disableClose = false;
      },
      error: error => {
        this.error.set(error?.error?.message ?? 'Test run failed. Please try again.');
        this.loading.set(false);
        this.dialogRef.disableClose = false;
      },
    });
  }
}
